import { motion } from "framer-motion";
import { ArrowUpRight, Wrench, Flame, Siren, Droplets, Hammer, Waves } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const Card = ({ id, icon: Icon, title, text, onQuote, service, className = "", img, imgAlt, imgPos, ourWork, imgs, dark = false, children }) => (
  <motion.button
    type="button"
    id={id === "hot-water" ? "hot-water" : undefined}
    data-testid={`service-card-${id}`}
    onClick={() => onQuote(service)}
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 260, damping: 22 }}
    className={`group relative flex min-h-[15rem] flex-col justify-between overflow-hidden rounded-[1.75rem] p-7 text-left shadow-[0_20px_50px_-30px_rgba(15,43,91,0.45)] ${dark || img || imgs ? "bg-[#0F2B5B] text-white" : "bg-white text-[#0F2B5B]"} ${className}`}
  >
    {imgs && (
      <>
        <div className="absolute inset-0 grid grid-cols-2 gap-1">
          {imgs.map((s) => (
            <img key={s} src={s} alt="iStore heat pump hot water system installed by Plumberoo" className="h-full w-full object-cover object-[center_45%] transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F42] via-[#0B1F42]/60 to-transparent" />
        <span className="absolute right-16 top-[2.4rem] rounded-full bg-white/90 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-[#0F2B5B]">Our work</span>
      </>
    )}
    {img && (
      <>
        <img src={img} alt={imgAlt || ""} className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105 ${imgPos || ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F42] via-[#0B1F42]/70 to-[#0B1F42]/10" />
        {ourWork && <span className="absolute right-16 top-[2.4rem] rounded-full bg-white/90 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-[#0F2B5B]">Our work</span>}
      </>
    )}
    <div className="relative flex items-start justify-between">
      <span className="grid h-14 w-14 place-items-center rounded-full bg-[#FF6B00] text-white shadow-lg transition-transform duration-500 group-hover:rotate-[-12deg]">
        <Icon className="h-6 w-6" />
      </span>
      <ArrowUpRight className={`h-6 w-6 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 ${img || dark || imgs ? "text-white" : "text-[#0F2B5B]"}`} />
    </div>
    <div className="relative mt-10">
      <h3 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h3>
      <p className={`mt-2 max-w-md text-sm sm:text-base ${img || dark || imgs ? "text-white/85" : "text-slate-600"}`}>{text}</p>
      {children}
    </div>
  </motion.button>
);

export const Services = ({ onQuote }) => (
  <section id="services" className="bg-[#F0F5FA] py-24 lg:py-32" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
      <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <Reveal>
          <Eyebrow>What we fix</Eyebrow>
          <h2 className="font-display max-w-2xl text-4xl font-extrabold leading-[1] tracking-tight text-[#0F2B5B] sm:text-6xl">Every job, done properly the first time.</h2>
        </Reveal>
        <Reveal delay={0.1}><p className="max-w-sm text-base text-slate-600 md:text-lg">Tap any service to start a quote — we'll come back to you with a clear, upfront price.</p></Reveal>
      </div>
      <Reveal>
        <div data-testid="services-bento-grid" className="grid auto-rows-[minmax(15rem,auto)] grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          <Card id="blocked-drains" service="Blocked Drains" icon={Waves} title="Blocked Drains" img="/images/work-detail.jpg" imgAlt="Plumber clearing and repairing a blocked drain"
            text="Expert clearing & repairs for sinks, toilets, showers and stormwater — tracked down and sorted fast." onQuote={onQuote} className="md:col-span-2 lg:row-span-2 lg:min-h-[32rem]" />
          <Card id="hot-water" service="Hot Water" icon={Flame} title="Hot Water Systems" imgs={["/images/hot-water-install-1.jpg", "/images/hot-water-install-2.jpg"]}
            text="Installation, service & replacement of gas, electric, solar and heat pump systems." onQuote={onQuote} className="md:col-span-2" />
          <Card id="emergency" service="Emergency Repair" icon={Siren} title="Emergency Repairs" img="/images/emergency-leak.jpg" imgAlt="Water spraying from a burst brass pipe fitting" imgPos="object-[center_60%]"
            text="Burst pipes & leaks — call and we'll get to you." onQuote={onQuote} />
          <Card id="gas-fitting" service="Gas Fitting" icon={Wrench} title="Gas Fitting" img="/images/gas-fitting.jpg" imgAlt="Blue flames on a gas cooktop burner"
            text="Certified gas line installation & service." onQuote={onQuote} />
          <Card id="filtration" service="Water Filtration" icon={Droplets} title="Water Filtration" img="/images/filtration-install.jpg" imgAlt="Stainless steel whole-house water filtration system installed by Plumberoo" imgPos="object-[center_35%]" ourWork
            text="Whole-house and under-sink systems for cleaner Perth water." onQuote={onQuote} className="md:col-span-1 lg:col-span-2" />
          <Card id="renovations" service="Renovations & Commercial" icon={Hammer} title="Renovations & Commercial" img="/images/renovation-bathroom.jpg" imgAlt="Modern renovated bathroom with timber vanity and walk-in shower" imgPos="object-[center_40%]"
            text="Residential, commercial and renovation plumbing from rough-in to fit-off." onQuote={onQuote} className="md:col-span-1 lg:col-span-2" />
        </div>
      </Reveal>
    </div>
  </section>
);
